<template>
  <div
    class="course-categories"
    :class="{
      'electron-course-categories': isElectron,
    }"
  >
    <!-- Section Header (使用通用组件) -->
    <SectionHeader
      title="热门课程分类"
      subtitle="探索您感兴趣的领域，开启技能提升之旅"
      actionText="全部分类"
      :showAction="true"
      @action="$emit('openDrawer')"
    >
      <template #action-icon>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>
      </template>
    </SectionHeader>

    <!-- 课程分类 -->
    <div :class="{ 'is-h5': isH5 }">
      <div v-if="categories.length" class="category-grid grid grid-cols-4 md:grid-cols-4 lg:grid-cols-8 gap-2 md:gap-4 mb-6">
        <div
          v-for="(item, index) in categories"
          :key="index"
          class="category-item group flex flex-col items-center justify-center p-2 py-4 md:p-4 md:py-6 bg-white dark:bg-slate-800 rounded-2xl md:rounded-3xl shadow-[0_2px_8px_rgba(0,0,0,0.04)] dark:shadow-none cursor-pointer transition-all duration-300 hover:shadow-[0_12px_24px_rgba(0,0,0,0.08)] dark:hover:shadow-[0_12px_24px_rgba(0,0,0,0.5)] hover:-translate-y-1 h-full dark:border dark:border-slate-700"
          @click="jumpCategory(item)"
        >
          <div
            class="category-icon-bg w-10 h-10 md:w-12 md:h-12 rounded-2xl flex items-center justify-center mb-2 md:mb-3 transition-all duration-300 shadow-sm group-hover:rotate-6 group-hover:text-white"
            :class="item.colorClasses"
          >
            <svg width="20" height="20" class="category-icon md:w-[22px] md:h-[22px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" v-html="item.svgPath"></svg>
          </div>
          <p class="category-text font-bold text-slate-700 dark:text-slate-200 text-center text-xs md:text-sm whitespace-nowrap transition-colors duration-200 group-hover:text-slate-900 dark:group-hover:text-white">{{ item.name }}</p>
        </div>
      </div>

      <!-- 机构入驻横幅 -->
      <div class="settle-banner group relative w-full bg-slate-900 dark:bg-slate-800 rounded-3xl py-5 px-6 md:py-6 md:px-8 flex items-center justify-between overflow-hidden cursor-pointer shadow-xl shadow-slate-200/50 dark:shadow-none transition-all duration-300 hover:shadow-[0_24px_48px_-12px_rgba(16,185,129,0.2)] hover:-translate-y-1" @click="handleClickSettle">
        <!-- Background decorations -->
        <div class="absolute top-0 right-0 w-64 h-64 bg-brand-500/20 rounded-full blur-[48px] translate-x-1/2 -translate-y-1/2 transition-all duration-500 group-hover:bg-brand-500/30"></div>
        <div class="absolute bottom-0 left-0 w-40 h-40 bg-blue-500/10 rounded-full blur-[32px] -translate-x-1/2 translate-y-1/2"></div>

        <div class="relative z-10 flex items-center gap-4 md:gap-6">
          <div class="w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-white/10 flex items-center justify-center text-brand-400 flex-shrink-0 transition-all duration-300 shadow-inner ring-1 ring-white/10 group-hover:bg-brand-500 group-hover:text-white">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path><polyline points="9 22 9 12 15 12 15 22"></polyline></svg>
          </div>
          <div>
            <h3 class="text-lg md:text-xl font-black text-white flex items-center gap-2 mb-1">
              机构入驻通道
              <span class="text-[10px] md:text-xs font-bold text-slate-900 bg-brand-400 py-0.5 px-2 rounded-full inline-flex items-center gap-1 animate-pulse">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" stroke="none"><path d="M12 0L14.59 8.41L23 12L14.59 15.59L12 24L9.41 15.59L1 12L9.41 8.41Z"/></svg>
                免费入驻
              </span>
            </h3>
            <p class="text-slate-400 text-xs md:text-sm font-medium transition-colors duration-200 truncate md:whitespace-normal group-hover:text-slate-300">
              携手打造优质教育生态，共享千万级流量扶持
            </p>
          </div>
        </div>
        <div class="hidden sm:flex relative z-10 items-center gap-2 bg-white/10 text-white py-3 px-6 rounded-full font-bold transition-all duration-200 border border-white/5 flex-shrink-0 shadow-md group-hover:bg-white/20 group-hover:border-brand-500/50">
          立即加入
          <svg class="transition-transform duration-200 group-hover:translate-x-1" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline></svg>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { fastCreateOrganization } from "@saas/server-api/src/organization";
import { getCdnUrl, isH5 } from "@saas/components";
import SectionHeader from "./SectionHeader.vue";

// Tailwind class-based color palette (matching design)
const COLOR_PALETTE = [
  { bg: "bg-blue-50 dark:bg-blue-900/20", text: "text-blue-600 dark:text-blue-400", hover: "group-hover:bg-blue-500" },
  { bg: "bg-orange-50 dark:bg-orange-900/20", text: "text-orange-600 dark:text-orange-400", hover: "group-hover:bg-orange-500" },
  { bg: "bg-purple-50 dark:bg-purple-900/20", text: "text-purple-600 dark:text-purple-400", hover: "group-hover:bg-purple-500" },
  { bg: "bg-green-50 dark:bg-green-900/20", text: "text-green-600 dark:text-green-400", hover: "group-hover:bg-green-500" },
  { bg: "bg-pink-50 dark:bg-pink-900/20", text: "text-pink-600 dark:text-pink-400", hover: "group-hover:bg-pink-500" },
  { bg: "bg-cyan-50 dark:bg-cyan-900/20", text: "text-cyan-600 dark:text-cyan-400", hover: "group-hover:bg-cyan-500" },
  { bg: "bg-yellow-50 dark:bg-yellow-900/20", text: "text-yellow-600 dark:text-yellow-400", hover: "group-hover:bg-yellow-500" },
  { bg: "bg-indigo-50 dark:bg-indigo-900/20", text: "text-indigo-600 dark:text-indigo-400", hover: "group-hover:bg-indigo-500" },
];

// SVG paths for each category icon (from lucide icons)
const ICON_SVG_PATHS = {
  "公考": '<path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/><path d="m15 5 4 4"/>',
  "教资教招": '<path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 10 3 12 0v-5"/>',
  "考研": '<path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>',
  "财会金融": '<line x1="3" y1="22" x2="21" y2="22"/><line x1="6" y1="18" x2="6" y2="11"/><line x1="10" y1="18" x2="10" y2="11"/><line x1="14" y1="18" x2="14" y2="11"/><line x1="18" y1="18" x2="18" y2="11"/><polygon points="12 2 20 7 4 7"/>',
  "设计制作": '<circle cx="13.5" cy="6.5" r=".5"/><circle cx="17.5" cy="10.5" r=".5"/><circle cx="8.5" cy="7.5" r=".5"/><circle cx="6.5" cy="12.5" r=".5"/><path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z"/>',
  "IT互联网": '<path d="m18 16 4-4-4-4"/><path d="m6 8-4 4 4 4"/><path d="m14.5 4-5 16"/>',
  "求职面试": '<rect x="2" y="7" width="20" height="14" rx="2" ry="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>',
  "个人提升": '<polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/>',
};

// 通用图标（用于没有专属图标的分类）
const DEFAULT_SVG_PATH = '<path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>';

// 默认分类（无用户数据时的回退）
const DEFAULT_CATEGORIES = [
  { name: "公考" },
  { name: "教资教招" },
  { name: "考研" },
  { name: "财会金融" },
  { name: "设计制作" },
  { name: "IT互联网" },
  { name: "求职面试" },
  { name: "个人提升" },
];

export default {
  props: {
    userCategories: {
      type: Array,
      default: () => [],
    },
  },
  data: function () {
    return {
      isH5: isH5(),
      isElectron: false,
      adminUrl: process.env.VUE_APP_ADMIN_URL || "/biz",
      settleUrl: (process.env.VUE_APP_SAAS_WEB_URL || "") + "/org-settled",
    };
  },
  created() {
    this.isElectron = window.isElectron;
  },
  computed: {
    ...mapGetters(["isAuthenticated", "user"]),
    isSettle() {
      return this.user && this.user.org_id;
    },
    categories() {
      const source = this.userCategories.length
        ? this.userCategories.map(c => ({ name: c.category_name }))
        : DEFAULT_CATEGORIES;
      return source.slice(0, 8).map((item, index) => {
        const palette = COLOR_PALETTE[index % COLOR_PALETTE.length];
        return {
          ...item,
          colorClasses: `${palette.bg} ${palette.text} ${palette.hover}`,
          svgPath: ICON_SVG_PATHS[item.name] || DEFAULT_SVG_PATH,
        };
      });
    },
  },
  methods: {
    jumpUrl(item) {
      if (item.type === "download") {
        let url = `/download`;
        if (window.isElectron) {
          this.$router.push(url);
        } else {
          window.open(url);
        }
      }
    },
    jumpCategory(item) {
      if (this.isElectron) {
        this.$router.push(`/search-result?keyword=${item.name}`);
      } else {
        window.open(`/search-result?keyword=${item.name}`);
      }
    },
    handleClickSettle() {
      if (this.isAuthenticated && this.isSettle) {
        this.openAdmin();
      } else if (this.isAuthenticated && !this.isSettle) {
        fastCreateOrganization()
          .then((res) => {
            if (res.code === 1) {
              this.openAdmin();
            } else {
              this.$message.error(res.message);
            }
          })
          .catch(() => {
            this.$message.error("机构创建失败，请稍后重试");
          });
      } else {
        this.openSettle();
      }
    },
    openSettle() {
      if (this.isElectron) {
        window.openUrlByExternal(this.settleUrl);
      } else {
        this.$router.push("/org-settled");
      }
    },
    openAdmin() {
      if (this.isElectron) {
        window.openUrlByExternal(this.adminUrl);
      } else {
        window.open(this.adminUrl, "_blank");
      }
    },
  },
  components: {
    SectionHeader,
  },
};
</script>

<style lang="scss" scoped>
.course-categories {
  padding: 20px 0 16px;
}

/* H5 移动端适配：覆盖 Tailwind rem 导致随 root font-size 放大的问题 */
.is-h5 {
  .category-grid {
    gap: 8px !important;
    margin-bottom: 16px !important;
  }
  
  .category-item {
    padding: 12px 8px !important;
    border-radius: 12px !important;
  }

  .category-icon-bg {
    width: 36px !important;
    height: 36px !important;
    border-radius: 10px !important;
    margin-bottom: 8px !important;
  }

  .category-icon {
    width: 20px !important;
    height: 20px !important;
  }

  .category-text {
    font-size: 12px !important;
    line-height: 1.2 !important;
  }

  .settle-banner {
    padding: 16px 20px !important;
    border-radius: 16px !important;

    h3 {
      font-size: 16px !important;
      line-height: 1.4 !important;
      margin-bottom: 4px !important;
      gap: 6px !important;
      span {
        font-size: 10px !important;
        padding: 2px 6px !important;
        gap: 2px !important;
        svg { width: 10px !important; height: 10px !important; }
      }
    }

    p {
      font-size: 12px !important;
      line-height: 1.4 !important;
    }

    .w-14 {
      width: 44px !important;
      height: 44px !important;
      border-radius: 12px !important;
      svg { width: 22px !important; height: 22px !important; }
    }

    .gap-4 {
      gap: 12px !important;
    }

    .w-64, .w-40 { display: none; }
  }
}
</style>
